import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ChatConversationController } from 'src/@core/APIs/ChatConversationController';
import { ChatInteractiveSelection, ChatMessage } from '../models/chat-message.model';
import { ChatPresetQuestion } from '../models/chat-preset-question.model';
import { ChatRequestPayload, ChatResponse, ChatUsageBalance } from '../models/chat-session-api.model';
import { ChatSession } from '../models/chat-session.model';
import { ChatUiError } from '../models/chat-ui-error.model';
import { ChatSessionTransportService } from './chat-session-transport.service';
import {
  buildRequestContext,
  extractApplicationError,
  extractAssistantContent,
  extractAssistantMessage,
  extractConversationId,
  extractConversationTitle,
  extractInteractiveOptions,
  extractUsageBalance,
  getAuthToken,
  getCurrentUserId,
  mapError,
  toConversationDetail,
  toConversationSummaryList,
  toPresetQuestionList,
} from '../utils/chat-session-api.utils';
import {
  buildChatMessage,
  buildChatSession,
  compactEmptyDraftSessions,
  mapConversationDetail,
  mapConversationMessage,
  mapConversationSummary,
  mergeChatSessions,
  normalizeChatSessionTitle,
} from '../utils/chat-session-state.utils';
import {
  appendMessageToSessions,
  findReusableEmptyDraftSession,
  findSession,
  linkConversationIdInSessions,
  persistSessions,
  readStoredSessions,
  renameSessionInSessions,
  replaceSessionInSessions,
  updateMessageRatingInSessions,
} from '../utils/chat-session-storage.utils';

@Injectable({ providedIn: 'root' })
export class ChatSessionService {
  private readonly sessions$ = new BehaviorSubject<ChatSession[]>([]);
  private readonly activeSessionId$ = new BehaviorSubject<string | null>(null);
  private readonly sending$ = new BehaviorSubject<boolean>(false);
  private readonly error$ = new BehaviorSubject<ChatUiError | null>(null);
  private readonly usageBalance$ = new BehaviorSubject<ChatUsageBalance | null>(null);
  private readonly presetQuestions$ = new BehaviorSubject<ChatPresetQuestion[]>([]);
  private readonly presetQuestionsLoading$ = new BehaviorSubject<boolean>(false);
  private readonly presetQuestionsLoadFailed$ = new BehaviorSubject<boolean>(false);
  private initialized = false;
  private initializedUserId: string | null = null;
  private usageBalanceUserId: string | null = null;
  private presetQuestionsUserId: string | null = null;
  private readonly storageKeyPrefix = 'forlab_chat_sessions';
  private readonly activeKeyPrefix = 'forlab_chat_active';

  constructor(
    private httpClient: HttpClient,
    private chatTransport: ChatSessionTransportService,
  ) {
    this.initializeFromStoredSessions(false);
  }

  get sessionsChanges() {
    return this.sessions$.asObservable();
  }

  get activeSessionChanges() {
    return this.activeSessionId$.asObservable();
  }

  get sendingChanges() {
    return this.sending$.asObservable();
  }

  get errorChanges() {
    return this.error$.asObservable();
  }

  get usageBalanceChanges() {
    return this.usageBalance$.asObservable();
  }

  get presetQuestionsChanges() {
    return this.presetQuestions$.asObservable();
  }

  get presetQuestionsLoadingChanges() {
    return this.presetQuestionsLoading$.asObservable();
  }

  get presetQuestionsLoadFailedChanges() {
    return this.presetQuestionsLoadFailed$.asObservable();
  }

  refreshSessions(force = false) {
    const currentUserId = this.getCurrentUserId();
    this.loadUsageBalance(true);
    this.loadPresetQuestions(force);

    if (!force && this.initialized && this.initializedUserId === currentUserId) {
      return;
    }

    this.initialized = false;
    this.initializedUserId = null;
    this.initializeSessions();
  }

  startNewSession(title?: string) {
    if (!`${title || ''}`.trim()) {
      const existingEmptyDraft = findReusableEmptyDraftSession(this.sessions$.value, this.activeSessionId$.value);
      if (existingEmptyDraft) {
        this.activeSessionId$.next(existingEmptyDraft.id);
        this.persistSessions();
        return;
      }
    }

    const session = buildChatSession(title);
    this.sessions$.next([session, ...this.sessions$.value]);
    this.activeSessionId$.next(session.id);
    this.persistSessions();
  }

  selectSession(sessionId: string) {
    const exists = this.sessions$.value.some((session) => session.id === sessionId);
    if (exists) {
      this.activeSessionId$.next(sessionId);
      this.persistSessions();
      const session = findSession(this.sessions$.value, sessionId);
      if (session?.conversationId) {
        this.loadConversation(session.id, session.conversationId);
      }
    }
  }

  deleteSession(sessionId: string) {
    const session = findSession(this.sessions$.value, sessionId);
    if (!session) {
      return;
    }

    if (!session.conversationId) {
      this.removeSessionLocally(sessionId);
      return;
    }

    const headers = this.buildAuthHeaders();
    if (!headers) {
      this.removeSessionLocally(sessionId);
      return;
    }

    this.chatTransport.deleteConversation(session.conversationId, headers, {
      next: () => this.removeSessionLocally(sessionId),
      error: () => this.removeSessionLocally(sessionId),
    });
  }

  sendMessage(content: string, presetQuestionId?: string, interactiveSelection?: ChatInteractiveSelection) {
    const trimmed = content.trim();
    if (!trimmed) {
      return;
    }

    const session = this.ensureActiveSession();
    const userMessage = buildChatMessage('user', trimmed);
    const history = [...session.messages, userMessage];
    this.appendMessage(session.id, userMessage);
    this.renameSessionIfNeeded(session.id, trimmed);
    this.sending$.next(true);
    this.error$.next(null);

    const payload = this.buildRequestPayload(session, trimmed, history, presetQuestionId, interactiveSelection);
    const headers = this.buildAuthHeaders();

    this.sendChatRequest(session.id, payload, headers);
  }

  retryAssistantMessage(messageId: string) {
    if (this.sending$.value) {
      return;
    }

    const session = findSession(this.sessions$.value, this.activeSessionId$.value);
    if (!session) {
      return;
    }

    const assistantIndex = session.messages.findIndex(
      (message) => message.id === messageId && message.role === 'assistant',
    );
    if (assistantIndex < 0) {
      return;
    }

    const previousUserMessage = [...session.messages]
      .slice(0, assistantIndex)
      .reverse()
      .find((message) => message.role === 'user');
    const prompt = previousUserMessage?.content?.trim() || '';
    if (!prompt) {
      return;
    }

    this.resendAssistantResponse(session, prompt);
  }

  rateAssistantMessage(messageId: string, feedback: 'good' | 'bad' | null) {
    const session = this.sessions$.value.find((item) => item.messages.some((message) => message.id === messageId));
    if (!session) {
      return;
    }

    const headers = this.buildAuthHeaders();
    if (!headers) {
      return;
    }

    this.httpClient.post(
      ChatConversationController.RateMessage,
      { messageId, responseRating: feedback },
      { headers }
    ).subscribe({
      next: () => {
        this.updateMessageRating(session.id, messageId, feedback);
      },
      error: (err) => {
        this.error$.next(mapError(err));
      },
    });
  }

  clearError() {
    this.error$.next(null);
  }

  private resendAssistantResponse(session: ChatSession, prompt: string) {
    const history = [...session.messages];
    this.sending$.next(true);
    this.error$.next(null);

    const payload = this.buildRequestPayload(session, prompt, history);
    const headers = this.buildAuthHeaders();

    this.sendChatRequest(session.id, payload, headers);
  }

  private buildRequestPayload(
    session: ChatSession,
    message: string,
    history: ChatMessage[],
    presetQuestionId?: string,
    interactiveSelection?: ChatInteractiveSelection,
  ): ChatRequestPayload {
    return {
      sessionId: session.id,
      conversationId: session.conversationId,
      message,
      presetQuestionId: presetQuestionId?.trim() || undefined,
      interactiveSelection: interactiveSelection
        ? {
            type: interactiveSelection.type,
            values: [interactiveSelection.value],
          }
        : undefined,
      history: history.map(({ role, content }) => ({ role, content })),
      context: buildRequestContext(),
    };
  }

  private sendChatRequest(sessionId: string, payload: ChatRequestPayload, headers: HttpHeaders | undefined) {
    this.chatTransport.postChatMessage(payload, headers, {
      next: (response) => {
        const appError = extractApplicationError(response);
        if (appError) {
          this.sending$.next(false);
          this.loadUsageBalance(true);
          this.error$.next(appError);
          return;
        }

        this.applyChatResponse(sessionId, response);
        this.sending$.next(false);
      },
      error: (err) => {
        this.sending$.next(false);
        this.error$.next(mapError(err));
      },
    });
  }

  private applyChatResponse(sessionId: string, response: ChatResponse) {
    const conversationId = extractConversationId(response);
    if (conversationId) {
      this.linkConversationId(sessionId, conversationId);
    }

    const conversationTitle = extractConversationTitle(response);
    if (conversationTitle) {
      this.renameSession(sessionId, conversationTitle);
    }

    const assistantMessage = extractAssistantMessage(response);
    const assistantContent = assistantMessage?.content || extractAssistantContent(response);
    const interactive = extractInteractiveOptions(response);
    if (assistantContent) {
      this.appendMessage(
        sessionId,
        assistantMessage
          ? mapConversationMessage(assistantMessage)
          : { ...buildChatMessage('assistant', assistantContent), interactive }
      );
    }

    const usageBalance = extractUsageBalance(response);
    if (usageBalance) {
      this.usageBalance$.next(usageBalance);
    } else {
      this.loadUsageBalance(true);
    }
  }

  private buildAuthHeaders() {
    const token = this.getAuthToken();
    if (!token) {
      return undefined;
    }
    return new HttpHeaders({ Authorization: `${token}` });
  }

  private loadUsageBalance(force = false) {
    const currentUserId = this.getCurrentUserId();
    const headers = this.buildAuthHeaders();
    if (!headers || !currentUserId) {
      this.usageBalanceUserId = null;
      this.usageBalance$.next(null);
      return;
    }

    if (!force && this.usageBalance$.value && this.usageBalanceUserId === currentUserId) {
      return;
    }

    this.chatTransport.getUsageBalance(headers, {
      next: (response) => {
        this.usageBalanceUserId = currentUserId;
        this.usageBalance$.next(extractUsageBalance(response));
      },
      error: () => {
        if (force) {
          this.usageBalanceUserId = currentUserId;
          this.usageBalance$.next(null);
        }
      },
    });
  }

  private loadPresetQuestions(force = false) {
    const currentUserId = this.getCurrentUserId();
    const headers = this.buildAuthHeaders();
    if (!headers || !currentUserId) {
      this.presetQuestionsUserId = null;
      this.presetQuestions$.next([]);
      this.presetQuestionsLoading$.next(false);
      this.presetQuestionsLoadFailed$.next(false);
      return;
    }

    if (
      !force &&
      this.presetQuestionsUserId === currentUserId &&
      !this.presetQuestionsLoading$.value &&
      !this.presetQuestionsLoadFailed$.value
    ) {
      return;
    }

    this.presetQuestionsLoading$.next(true);
    this.presetQuestionsLoadFailed$.next(false);

    this.chatTransport.getPresetQuestions(headers, {
      next: (response) => {
        this.presetQuestionsUserId = currentUserId;
        this.presetQuestions$.next(toPresetQuestionList(response));
        this.presetQuestionsLoading$.next(false);
      },
      error: () => {
        this.presetQuestionsUserId = currentUserId;
        this.presetQuestions$.next([]);
        this.presetQuestionsLoading$.next(false);
        this.presetQuestionsLoadFailed$.next(true);
      },
    });
  }

  private ensureActiveSession(): ChatSession {
    const activeId = this.activeSessionId$.value;
    if (activeId) {
      const existing = findSession(this.sessions$.value, activeId);
      if (existing) {
        return existing;
      }
    }
    const first = this.sessions$.value[0];
    if (first) {
      this.activeSessionId$.next(first.id);
      return first;
    }
    const session = buildChatSession('New chat');
    this.sessions$.next([session]);
    this.activeSessionId$.next(session.id);
    return session;
  }

  private appendMessage(sessionId: string, message: ChatMessage) {
    const sessions = appendMessageToSessions(this.sessions$.value, sessionId, message);
    this.sessions$.next(sessions);
    this.persistSessions();
  }

  private replaceSession(sessionId: string, update: Partial<ChatSession>) {
    const { sessions, changed } = replaceSessionInSessions(this.sessions$.value, sessionId, update);

    if (!changed) {
      return;
    }

    this.sessions$.next(sessions);
    this.persistSessions();
  }

  private updateMessageRating(sessionId: string, messageId: string, rating: 'good' | 'bad' | null): void {
    const sessions = updateMessageRatingInSessions(this.sessions$.value, sessionId, messageId, rating);

    this.sessions$.next(sessions);
    this.persistSessions();
  }

  private removeSessionLocally(sessionId: string) {
    const remainingSessions = this.sessions$.value.filter((session) => session.id !== sessionId);
    this.sessions$.next(remainingSessions);

    if (this.activeSessionId$.value === sessionId) {
      const nextSession = remainingSessions[0] || null;
      this.activeSessionId$.next(nextSession?.id || null);
      if (nextSession?.conversationId) {
        this.loadConversation(nextSession.id, nextSession.conversationId);
      }
    }
    this.persistSessions();
  }

  private initializeFromStoredSessions(createDraftIfEmpty = true) {
    const stored = readStoredSessions(
      this.storageKeyPrefix,
      this.activeKeyPrefix,
      this.getCurrentUserId(),
    );
    const sessions = compactEmptyDraftSessions(stored.sessions, stored.activeId);
    if (sessions.length) {
      const activeId = sessions.some((session) => session.id === stored.activeId)
        ? stored.activeId
        : sessions[0]?.id || null;

      this.sessions$.next(sessions);
      this.activeSessionId$.next(activeId);
      if (sessions.length !== stored.sessions.length || activeId !== stored.activeId) {
        this.persistSessions();
      }
      return;
    }

    if (!createDraftIfEmpty) {
      this.sessions$.next([]);
      this.activeSessionId$.next(null);
      return;
    }

    const session = buildChatSession('New chat');
    this.sessions$.next([session]);
    this.activeSessionId$.next(session.id);
    this.persistSessions();
  }

  private initializeSessions() {
    const currentUserId = this.getCurrentUserId();
    if (this.initialized && this.initializedUserId === currentUserId) {
      return;
    }

    this.initialized = true;
    this.initializedUserId = currentUserId;

    const headers = this.buildAuthHeaders();
    if (headers && currentUserId) {
      this.loadSessionsFromBackend(headers);
      return;
    }

    this.initializeFromStoredSessions();
  }

  private renameSessionIfNeeded(sessionId: string, content: string) {
    const session = findSession(this.sessions$.value, sessionId);
    if (!session || session.messages.length > 1) {
      return;
    }
    const normalizedTitle = normalizeChatSessionTitle(content);
    const sessions = renameSessionInSessions(this.sessions$.value, sessionId, normalizedTitle);
    this.sessions$.next(sessions);
    this.persistSessions();
  }

  private renameSession(sessionId: string, title: string) {
    const normalizedTitle = normalizeChatSessionTitle(title);
    const session = findSession(this.sessions$.value, sessionId);
    if (!session || session.title === normalizedTitle) {
      return;
    }

    const sessions = renameSessionInSessions(this.sessions$.value, sessionId, normalizedTitle);
    this.sessions$.next(sessions);
    this.persistSessions();
  }

  private linkConversationId(sessionId: string, conversationId: string) {
    const normalizedConversationId = conversationId.trim();
    if (!normalizedConversationId) {
      return;
    }

    const { sessions, changed } = linkConversationIdInSessions(
      this.sessions$.value,
      sessionId,
      normalizedConversationId,
    );

    if (!changed) {
      return;
    }

    this.sessions$.next(sessions);
    this.persistSessions();
  }

  private loadSessionsFromBackend(headers: HttpHeaders) {
    const stored = readStoredSessions(
      this.storageKeyPrefix,
      this.activeKeyPrefix,
      this.getCurrentUserId(),
    );
    this.chatTransport.getConversations(headers, {
      next: (response) => {
        const remoteSessions = toConversationSummaryList(response).map((item) => mapConversationSummary(item));
        const localDrafts = compactEmptyDraftSessions(
          stored.sessions.filter((session) => !session.conversationId),
          stored.activeId,
        );
        const sessions = mergeChatSessions(localDrafts, remoteSessions);

        if (sessions.length === 0) {
          const session = buildChatSession('New chat');
          this.sessions$.next([session]);
          this.activeSessionId$.next(session.id);
          this.persistSessions();
          return;
        }

        this.sessions$.next(sessions);
        const requestedActiveId = stored.activeId;
        const activeId = sessions.some((session) => session.id === requestedActiveId)
          ? requestedActiveId
          : sessions[0].id;

        this.activeSessionId$.next(activeId);
        this.persistSessions();

        const activeSession = findSession(this.sessions$.value, activeId);
        if (activeSession?.conversationId) {
          this.loadConversation(activeSession.id, activeSession.conversationId, headers);
        }
      },
      error: () => {
        this.initializeFromStoredSessions();
      },
    });
  }

  private loadConversation(sessionId: string, conversationId: string, headers?: HttpHeaders) {
    const authHeaders = headers ?? this.buildAuthHeaders();
    if (!authHeaders) {
      return;
    }

    this.chatTransport.getConversationDetail(conversationId, authHeaders, {
      next: (response) => {
        const detail = toConversationDetail(response);
        if (!detail) {
          return;
        }

        this.replaceSession(sessionId, mapConversationDetail(detail, sessionId));
      },
      error: () => {
        // Keep the current snapshot if the detail request fails.
      },
    });
  }

  private persistSessions() {
    persistSessions(
      this.storageKeyPrefix,
      this.activeKeyPrefix,
      this.getCurrentUserId(),
      this.sessions$.value,
      this.activeSessionId$.value,
    );
  }

  private getCurrentUserId(): string | null { return getCurrentUserId(); }
  private getAuthToken() { return getAuthToken(); }
}
export type { ChatUsageBalance } from '../models/chat-session-api.model';
